import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { AreaChart } from "../../components/charts/area-chart";
import { usePredictionSummary, useRefreshMLData } from "../../hooks/use-ml";
import { useToast } from "../../hooks/use-toast";
import { clampPct } from "../../lib/utils";
import { getAIConfig } from "../ai-configuration";
import { 
  TrendingUp, 
  RefreshCw, 
  Loader2, 
  Settings, 
  AlertCircle, 
  History, 
  Brain, 
  AlertTriangle, 
  CheckCircle2, 
  Monitor, 
  Eye 
} from "lucide-react"; 

export default function PredictiveAnalytics() {
  const { toast } = useToast();
  const cfg = getAIConfig();
  const [selectedMetric, setSelectedMetric] = useState(() => cfg.predictive.defaultMetricFilter);

  // Fetch prediction data from backend (uses AI Configuration: limit, mode, hoursAhead)
  const { 
    data: predictionData, 
    isLoading, 
    error 
  } = usePredictionSummary(cfg.predictive.summaryLimit, cfg.general.mlMode, cfg.predictive.timeHorizonHours);
  
  const refreshMLData = useRefreshMLData();

  // Handle refresh
  const handleRefresh = async () => {
    try {
      await refreshMLData.mutateAsync();
      toast({
        title: "Data Refreshed",
        description: "Predictive analytics data has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Refresh Failed",
        description: "Failed to refresh prediction data. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Use real data or fallback to empty array
  const predictions = predictionData?.data?.predictions || [];
  const predictionStats = predictionData?.data || {
    total: 0,
    highRisk: 0,
    mediumRisk: 0,
    lowRisk: 0,
    avgConfidence: 0
  };

  // Filter predictions by metric type - removed duplicate declaration

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case "High": return "bg-red-100 text-red-800";
      case "Medium": return "bg-yellow-100 text-yellow-800";
      case "Low": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "increasing": return "↗";
      case "decreasing": return "↘";
      case "stable": return "→";
      default: return "→";
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case "increasing": return "text-red-500";
      case "decreasing": return "text-green-500";
      case "stable": return "text-blue-500";
      default: return "text-gray-500";
    }
  };

  const filteredPredictions = predictions.filter((prediction: any) => {
    if (selectedMetric !== "all" && prediction.metric !== selectedMetric) {
      return false;
    }
    return true;
  });

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-dark flex items-center">
              <TrendingUp className="h-6 w-6 text-green-500 mr-3" />
              Predictive Analytics
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              AI-powered forecasting and trend analysis for proactive network management
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshMLData.isPending || isLoading}
            >
              {refreshMLData.isPending || isLoading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              {refreshMLData.isPending || isLoading ? "Refreshing..." : "Refresh Predictions"}
            </Button>
            <Button size="sm">
              <Settings className="h-4 w-4 mr-1" />
              Configure Models
            </Button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading predictive analytics data...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Failed to load prediction data</h3>
              <p className="text-sm text-red-600 mt-1">
                There was an error loading the predictive analytics data. Please try refreshing.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {!isLoading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active Predictions</p>
                <p className="text-2xl font-bold text-gray-900">{predictionStats.total}</p>
              </div>
              <History className="h-6 w-6 text-gray-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Avg Confidence</p>
                <p className="text-2xl font-bold text-blue-500">{clampPct(predictionStats.avgConfidence)}%</p>
              </div>
              <Brain className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">High Risk</p>
                <p className="text-2xl font-bold text-red-500">{predictionStats.highRisk}</p>
              </div>
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Low Risk</p>
                <p className="text-2xl font-bold text-green-500">{predictionStats.lowRisk}</p>
              </div>
              <CheckCircle2 className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Main Content */}
      {!isLoading && !error && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Predictions List */}
        <div>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Performance Predictions</CardTitle>
                  <CardDescription>AI-powered forecasts for device performance</CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Metrics</option>
                    <option value="CPU Usage">CPU Usage</option>
                    <option value="Memory Usage">Memory Usage</option>
                    <option value="Disk Usage">Disk Usage</option>
                    <option value="Network Traffic">Network Traffic</option>
                    <option value="Response Time">Response Time</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredPredictions.length > 0 ? (
                <div className="space-y-4">
                  {filteredPredictions.map((prediction: any) => (
                  <div key={prediction.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Monitor className="h-5 w-5 text-gray-500" />
                          <span className="font-medium">{prediction.device}</span>
                          <Badge className={getRiskColor(prediction.riskLevel)}>
                            {prediction.riskLevel} Risk
                          </Badge>
                          <Badge className="bg-blue-100 text-blue-800">
                            {clampPct(prediction.confidence)}% confidence
                          </Badge>
                        </div>
                        <h4 className="font-medium text-gray-900 mb-1">{prediction.metric}</h4>
                        <p className="text-sm text-gray-600 mb-2">{prediction.recommendation}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-500">Current</p>
                        <p className="text-lg font-semibold">{prediction.currentValue}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Predicted</p>
                        <p className="text-lg font-semibold text-blue-600">{prediction.predictedValue}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Trend</p>
                        <p className={`text-lg font-semibold ${getTrendColor(prediction.trend)}`}>
                          {getTrendIcon(prediction.trend)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Time Horizon: {prediction.timeHorizon}</span>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <div className="text-center">
                    <History className="h-12 w-12 mb-2 text-gray-500" />
                    <p className="text-lg font-medium">No predictions available</p>
                    <p className="text-sm">Predictions will appear here when devices have sufficient data</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Prediction Charts */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Prediction Trends</CardTitle>
              <CardDescription>Historical data and predicted values</CardDescription>
            </CardHeader>
            <CardContent>
              {predictions.length > 0 ? (
                <div className="space-y-4">
                  {predictions.slice(0, 3).map((prediction: any) => (
                    <div key={prediction.id} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{prediction.device} - {prediction.metric}</span>
                        <Badge className={getRiskColor(prediction.riskLevel)}>
                          {prediction.riskLevel}
                        </Badge>
                      </div>
                      <div className="h-24">
                        {prediction.historicalData && prediction.historicalData.length > 0 ? (
                          <AreaChart
                            title=""
                            data={prediction.historicalData.map((point: any, index: number) => ({
                              time: new Date(Date.now() - (prediction.historicalData.length - index) * 60 * 60 * 1000).toLocaleTimeString(),
                              value: clampPct(point.cpu_usage ?? point.memory_usage ?? point.value ?? 0)
                            }))}
                            color={prediction.riskLevel === "High" ? "#ef4444" : 
                                   prediction.riskLevel === "Medium" ? "#f59e0b" : "#10b981"}
                            height={80}
                          />
                        ) : (
                          <div className="h-24 flex items-center justify-center text-gray-400 text-sm">
                            No historical data
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <div className="text-center">
                    <TrendingUp className="h-12 w-12 mb-2 text-gray-500" />
                    <p className="text-lg font-medium">No predictions available</p>
                    <p className="text-sm">Predictions will appear here when data is available</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Model Performance</CardTitle>
              <CardDescription>AI model accuracy and performance metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">CPU Prediction Model</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">94.2%</span>
                    <Badge className="bg-green-100 text-green-800">Excellent</Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Memory Prediction Model</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">91.7%</span>
                    <Badge className="bg-green-100 text-green-800">Excellent</Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Network Prediction Model</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">88.9%</span>
                    <Badge className="bg-yellow-100 text-yellow-800">Good</Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm font-medium">Disk Prediction Model</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">92.1%</span>
                    <Badge className="bg-green-100 text-green-800">Excellent</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
}
